import type { FanChart as FanData, YearPoint } from "@retirement-planner/contract";
import { useLanguage } from "../i18n/LanguageProvider";
import { formatMoney } from "../lib/format";

const W = 480;
const H = 200;
const PAD = 8;

// Money grows from a real 0 baseline, so scale the y-axis from 0 to the peak —
// an honest area chart where a rising nest egg reads as a rising filled region
// (a context-less bare line is what made this look "empty").
function scaleY(v: number, max: number): number {
  return H - PAD - (v / max) * (H - PAD * 2);
}
function scaleX(i: number, n: number): number {
  return PAD + i * ((W - PAD * 2) / Math.max(n - 1, 1));
}
function linePath(values: number[], max: number): string {
  return values
    .map((v, i) => `${i === 0 ? "M" : "L"}${scaleX(i, values.length).toFixed(1)},${scaleY(v, max).toFixed(1)}`)
    .join(" ");
}
/** Close the line down to the baseline for a filled area. */
function areaPath(values: number[], max: number): string {
  if (values.length === 0) return "";
  const base = H - PAD;
  const last = scaleX(values.length - 1, values.length);
  return `${linePath(values, max)} L${last.toFixed(1)},${base} L${PAD},${base} Z`;
}

function ChartDataTable({ series, label }: { series: YearPoint[]; label: string }) {
  const { t, locale } = useLanguage();
  return (
    <details className="chart-disclosure">
      <summary>{t("viewDataTable")}</summary>
      <table className="chart-table">
        <caption className="visually-hidden">{label}</caption>
        <thead>
          <tr>
            <th scope="col">{t("age")}</th>
            <th scope="col">{t("real")}</th>
          </tr>
        </thead>
        <tbody>
          {series.map((p) => (
            <tr key={p.age}>
              <td>{p.age}</td>
              <td>{formatMoney(p.real.amountMinorUnits, p.real.currency, locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function ChartTextSummary({ summary }: { summary: string }) {
  const { t } = useLanguage();
  return (
    <details className="chart-disclosure">
      <summary>{t("textSummary")}</summary>
      <p>{summary}</p>
    </details>
  );
}

/** Age + value endpoints under the chart, so the line always has context. */
function Endpoints({ first, last }: { first: YearPoint; last: YearPoint }) {
  const { t, locale } = useLanguage();
  return (
    <div className="chart-endpoints">
      <span>
        <span className="chart-endpoints__age">{t("age")} {first.age}</span>
        <span className="tabular">{formatMoney(first.real.amountMinorUnits, first.real.currency, locale)}</span>
      </span>
      <span className="chart-endpoints__to">
        <span className="chart-endpoints__age">{t("age")} {last.age}</span>
        <span className="tabular chart-endpoints__end">{formatMoney(last.real.amountMinorUnits, last.real.currency, locale)}</span>
      </span>
    </div>
  );
}

/** Real (inflation-adjusted) nest-egg area chart. */
export function ProjectionChart({ series, label }: { series: YearPoint[]; label: string }) {
  const { t, locale } = useLanguage();
  const values = series.map((p) => p.real.amountMinorUnits);
  const max = Math.max(...values, 1);
  const line = linePath(values, max);
  const area = areaPath(values, max);
  const first = series[0];
  const last = series[series.length - 1];
  const endX = scaleX(values.length - 1, values.length);
  const endY = scaleY(values[values.length - 1] ?? 0, max);
  const summary =
    first && last
      ? `${label}: ${formatMoney(first.real.amountMinorUnits, first.real.currency, locale)} (${t("age")} ${first.age}) → ${formatMoney(last.real.amountMinorUnits, last.real.currency, locale)} (${t("age")} ${last.age}).`
      : label;
  if (!first || !last) return <p className="state-panel">{summary}</p>;
  return (
    <figure className="chart-figure">
      <svg className="chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={summary}>
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} className="chart-baseline" />
        <path d={area} className="chart-area" />
        <path d={line} className="chart-line" fill="none" vectorEffect="non-scaling-stroke" />
        <circle cx={endX} cy={endY} r="3" className="chart-endpoint-dot" vectorEffect="non-scaling-stroke" />
      </svg>
      <Endpoints first={first} last={last} />
      <figcaption className="visually-hidden">{summary}</figcaption>
      <ChartTextSummary summary={summary} />
      <ChartDataTable series={series} label={label} />
    </figure>
  );
}

/** p10/p50/p90 fan: shaded band between pessimistic and optimistic, median on top. */
export function FanChart({ fan, label }: { fan: FanData; label: string }) {
  const { t, locale } = useLanguage();
  const allMax = Math.max(
    ...fan.p90.map((p) => p.real.amountMinorUnits),
    ...fan.p50.map((p) => p.real.amountMinorUnits),
    1,
  );
  const band = ((): string => {
    const hi = fan.p90.map((p) => p.real.amountMinorUnits);
    const lo = fan.p10.map((p) => p.real.amountMinorUnits);
    if (hi.length === 0) return "";
    const up = hi.map((v, i) => `${i === 0 ? "M" : "L"}${scaleX(i, hi.length).toFixed(1)},${scaleY(v, allMax).toFixed(1)}`).join(" ");
    const down = lo
      .map((_, i) => {
        const j = lo.length - 1 - i;
        return `L${scaleX(j, lo.length).toFixed(1)},${scaleY(lo[j], allMax).toFixed(1)}`;
      })
      .join(" ");
    return `${up} ${down} Z`;
  })();
  const p50 = linePath(fan.p50.map((p) => p.real.amountMinorUnits), allMax);
  const end = (pts: YearPoint[]) => pts[pts.length - 1];
  const summary =
    fan.p50.length > 0
      ? `${t("median")} ${formatMoney(end(fan.p50).real.amountMinorUnits, end(fan.p50).real.currency, locale)}; ${t("pessimistic")} ${formatMoney(end(fan.p10).real.amountMinorUnits, end(fan.p10).real.currency, locale)}; ${t("optimistic")} ${formatMoney(end(fan.p90).real.amountMinorUnits, end(fan.p90).real.currency, locale)}.`
      : label;
  if (fan.p50.length === 0) return <p className="state-panel">{label}</p>;
  return (
    <figure className="chart-figure">
      <svg className="chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={summary}>
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} className="chart-baseline" />
        <path d={band} className="chart-area" />
        <path d={p50} className="chart-line" fill="none" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="chart-endpoints">
        <span><span className="chart-endpoints__age">{t("pessimistic")}</span><span className="tabular">{formatMoney(end(fan.p10).real.amountMinorUnits, end(fan.p10).real.currency, locale)}</span></span>
        <span className="chart-endpoints__to"><span className="chart-endpoints__age">{t("optimistic")}</span><span className="tabular chart-endpoints__end">{formatMoney(end(fan.p90).real.amountMinorUnits, end(fan.p90).real.currency, locale)}</span></span>
      </div>
      <figcaption className="visually-hidden">{summary}</figcaption>
      <ChartTextSummary summary={summary} />
      <ChartDataTable series={fan.p50} label={`${label} (p50)`} />
    </figure>
  );
}
